import React, {
  createContext,
  ReactNode,
  useEffect,
  useReducer,
  useState,
} from "react";

import firebase from "firebase/app";
import "firebase/auth";
import "firebase/firestore";

import {
  FirebaseAuthContextType,
  ActionMap,
  AuthState,
  AuthUser,
} from "@/types/auth";
import { firebaseConfig } from "@/config";
import sessionActions from "actions/session";
import { Logout } from "@mui/icons-material";

const INITIALIZE = "INITIALIZE";

if (!firebase.apps.length) {
  firebase.initializeApp(firebaseConfig);
  firebase.firestore();
}

const initialState: AuthState = {
  isAuthenticated: false,
  isInitialized: false,
  user: null,
};

type AuthActionTypes = {
  [INITIALIZE]: {
    isAuthenticated: boolean;
    user: AuthUser;
  };
};

type FirebaseActions =
  ActionMap<AuthActionTypes>[keyof ActionMap<AuthActionTypes>];

const reducer = (state: AuthState, action: FirebaseActions) => {
  if (action.type === INITIALIZE) {
    const { isAuthenticated, user } = action.payload;
    return {
      ...state,
      isAuthenticated,
      isInitialized: true,
      user,
    };
  }

  return state;
};

const AuthContext = createContext<FirebaseAuthContextType | null>(null);

function AuthProvider({ children }: { children: ReactNode }) {
  const [profile, setProfile] = useState<
    AuthUser
  >();
  const [state, dispatch] = useReducer(reducer, initialState);

  useEffect(
    () =>
      firebase.auth().onAuthStateChanged(async(user) => {
        if (user) {
          const idToken = await user.getIdToken()
          try{
            const {data: fullUser} = await sessionActions.verifyToken({idToken})
            // const docRef = firebase.firestore().collection("users").doc(user.uid);
            // docRef
            //   .get()
            //   .then((doc) => {
            //     if (doc.exists) {
            //       setProfile(doc.data());
            //     }
            //   })
            //   .catch((error) => {
            //     console.error(error);
            //   });
            setProfile(fullUser);
            dispatch({
              type: INITIALIZE,
              payload: { isAuthenticated: true, user: fullUser },
            });
          }catch(error){
            signOut()
            console.log("error", error);
          }
        } else {
          dispatch({
            type: INITIALIZE,
            payload: { isAuthenticated: false, user: null },
          });
        }
      }),
    [dispatch]
  );

  const signIn = (email: string, password: string) =>
    firebase.auth().signInWithEmailAndPassword(email, password);

  const signInWithGoogle = () => {
    const provider = new firebase.auth.GoogleAuthProvider();
    return firebase.auth().signInWithPopup(provider);
  };

  const signInWithFaceBook = () => {
    const provider = new firebase.auth.FacebookAuthProvider();
    return firebase.auth().signInWithPopup(provider);
  };

  const signInWithTwitter = () => {
    const provider = new firebase.auth.TwitterAuthProvider();
    return firebase.auth().signInWithPopup(provider);
  };

  // const signUp = (
  //   email: string,
  //   password: string,
  //   firstName: string,
  //   lastName: string
  // ) =>
  //   firebase
  //     .auth()
  //     .createUserWithEmailAndPassword(email, password)
  //     .then((res) => {
  //       firebase
  //         .firestore()
  //         .collection("users")
  //         .doc(res.user?.uid)
  //         .set({
  //           uid: res.user?.uid,
  //           email,
  //           displayName: `${firstName} ${lastName}`,
  //           photoURL: res.user?.photoURL
  //         });
  //     });

  const signOut = async () => {
    await firebase.auth().signOut();
  };

  const resetPassword = async (email: string) => {
    await firebase.auth().sendPasswordResetEmail(email);
  };

  const signUp = async (email: string, password: string) => {
    try{
      await firebase.auth().createUserWithEmailAndPassword(email, password);
    }catch(error){
      throw error;
    }
  }

  const auth : AuthUser = { ...state.user };
  
  return (
    <AuthContext.Provider
      value={{
        ...state,
        method: "firebase",
        user: auth,
        signIn,
        signUp,
        signInWithGoogle,
        signInWithFaceBook,
        signInWithTwitter,
        signOut,
        resetPassword,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
}

export { AuthContext, AuthProvider };
